/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect} from "react"
import cx from "classnames"
import {KlassappTable} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {KlassappTableProps} from "types/common"
import styles from "./PaymentPlanItemTable.module.css"
import moment from "moment/moment"
import {useModel} from "hooks"
import {formatCurrency, formatDateOnly, isPaymentPlanItemUpNextForRecurring, toDateOnly} from "helpers"
import {PaymentPlan, PaymentPlanItem, PaymentPlanItemStatus} from "types/student-account/payment-plan"
import {StudentRecurringPayment} from "types/student-account/student-payment"
import {useTranslation} from "react-i18next"
import {routing} from "helpers/routing"

type Props = KlassappTableProps & {
    paymentPlan: Partial<PaymentPlan>
    recurringPayments?: StudentRecurringPayment[]
}

const PaymentPlanItemTable = ({
    columns,
    data,
    allFields,
    fields,
    menuActions,
    isLoading,
    dispatch,
    dispatchFunc,
    onChangeFields,
    onDraggableColumn,
    onChangeAllFields,
    paymentPlan,
    recurringPayments
}: Props) => {
    const {t} = useTranslation(["studentAccount", "common"])
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()

    const getFields = useCallback(
        () => [
            t("payments.column.id"),
            t("payments.column.originalAmount"),
            t("payments.column.paidAmount"),
            t("payments.column.dueDate"),
            t("payments.column.paidDate"),
            t("payments.column.paymentId"),
            t("payments.column.status")
        ],
        []
    )

    const getColumns = useCallback(() => {
        return [
            {
                title: t("payments.column.id"),
                field: "paymentPlanItemId",
                render: (value) => <span className={styles.narrowCell}>{value}</span>
            },
            {
                title: t("payments.column.originalAmount"),
                field: "amount",
                render: (value) => <span className={styles.narrowCell}>{formatCurrency(value)}</span>,
                renderText: (value: number) => (value ?? 0).toFixed(2)
            },
            {
                title: t("payments.column.paidAmount"),
                field: "paidAmount",
                render: (value, record: PaymentPlanItem) => (
                    <span className={styles.narrowCell}>{formatCurrency(Math.min(record.amount, value))}</span>
                ),
                renderText: (value: number, record: PaymentPlanItem) => Math.min(record.amount, value).toFixed(2)
            },
            {
                title: t("payments.column.dueDate"),
                field: "dueDate",
                render: (value) => <span className={styles.narrowCell}>{formatDateOnly(value, userDateFormat)}</span>
            },
            {
                title: t("payments.column.paidDate"),
                field: "paidAt",
                render: (value) => (
                    <span className={styles.narrowCell}>{value ? formatDateOnly(value, userDateFormat) : ""}</span>
                )
            },
            {
                title: t("payments.column.paymentId"),
                field: "paymentId",
                render: (value, record: PaymentPlanItem) => {
                    const paymentItems = paymentPlan.paymentItems?.filter(
                        (item) => item.paymentPlanItemId === record.paymentPlanItemId
                    )
                    return (
                        <ul className="list-inside">
                            {paymentItems?.map((paymentItem) => {
                                const payment = paymentPlan.payments?.find(
                                    (payment) =>
                                        payment.paymentId === paymentItem.paymentId && payment.status === "success"
                                )
                                if (!payment) return null
                                return (
                                    <li key={paymentItem.paymentId}>
                                        <a
                                            href={routing.studentAccount.student.ledger.payment.detail(
                                                payment.studentProfileId,
                                                payment.paymentId
                                            )}>
                                            Payment #{payment.sisId ?? payment.paymentId}
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    )
                }
            },
            {
                title: t("payments.column.status"),
                field: "amount",
                render: (_, record: PaymentPlanItem) => {
                    if (record.status === PaymentPlanItemStatus.Paid) {
                        return <span className={`${styles.webhookStatus} ${styles.webhookStatus__paid}`}>Paid</span>
                    }
                    if (record.status === PaymentPlanItemStatus.Failed) {
                        return <span className={`${styles.webhookStatus} ${styles.webhookStatus__failed}`}>Failed</span>
                    }
                    if (record.status === PaymentPlanItemStatus.PastDue) {
                        return (
                            <span className={`${styles.webhookStatus} ${styles.webhookStatus__past_due}`}>
                                Past Due
                            </span>
                        )
                    }
                    if (record.status === PaymentPlanItemStatus.Canceled) {
                        return (
                            <span className={`${styles.webhookStatus} ${styles.webhookStatus__canceled}`}>
                                Canceled
                            </span>
                        )
                    }
                    // Only the item a recurring payment is actually about to bill next is
                    // "In Progress" — not every unpaid item on the plan. Takes priority over the
                    // raw due-date heuristic below since the recurring payment is already set up
                    // to handle it regardless of due-date proximity.
                    if (
                        record.paidAmount < record.amount &&
                        isPaymentPlanItemUpNextForRecurring(recurringPayments ?? [], record.paymentPlanItemId)
                    ) {
                        return <span className={cx(styles.status, "in_progress")}>In Progress</span>
                    }
                    if (!record.paidAmount) {
                        const dueDate = moment(toDateOnly(record.dueDate))
                        const numDays = Math.abs(moment().diff(dueDate, "day"))
                        const isPastDue = dueDate.isBefore(Date.now(), "day")
                        const isDue = moment().add(20, "day").isAfter(dueDate)
                        if (isPastDue)
                            return (
                                <span className={styles.pastDue}>
                                    Past Due{" "}
                                    {t(!numDays ? "paymentPlan.today" : "paymentPlan.numDays", {
                                        count: numDays
                                    })}
                                </span>
                            )
                        if (isDue)
                            return (
                                <span className={styles.due}>
                                    Due{" "}
                                    {t(!numDays ? "paymentPlan.today" : "paymentPlan.numDays", {
                                        count: numDays
                                    })}
                                </span>
                            )
                        return "Pending"
                    }
                    if (record.paidAmount < record.amount) return "Partially Paid"
                    return <span className={cx(styles.status, "completed")}>Completed</span>
                }
            }
        ]
    }, [paymentPlan, recurringPayments])

    const onClickRowItem = useCallback(() => {}, [])

    const getMenuActions = useCallback(() => [], [])

    const getData = useCallback(() => {
        const data = paymentPlan.items || []
        dispatch({total: data.length, data: data.map((item) => ({...item, id: item.paymentPlanItemId}))})
    }, [paymentPlan.items])

    useEffect(() => {
        getData()
    }, [paymentPlan?.items])

    useEffect(() => {
        dispatch({isClassComponent: false, isHideMenuActions: true})
        dispatchFunc([
            {key: "getPageTitle", func: () => ""},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getMenuActions", func: getMenuActions},
            {key: "getListData", func: getData}
        ])
    }, [])

    return (
        <div className={styles.wrap}>
            <h3 className={styles.title}>PAYMENTS</h3>
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                menuActions={menuActions}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn={false}
                onClickRowItem={onClickRowItem}
                onChangeFields={onChangeFields}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />
        </div>
    )
}

export default KlassappTableHOC(PaymentPlanItemTable)
